<!-- File: components/TopBar.vue -->
<template>
  <header 
    class="fixed top-0 left-0 w-full bg-background/80 backdrop-blur-sm border-b transition-transform duration-300 ease-in-out z-50"
    :class="{ '-translate-y-full': isHidden }"
  >
    <div class="w-full px-4 py-2 flex justify-between items-center bg-background/80 backdrop-blur-sm">
      <div class="flex items-center space-x-4">
        <NuxtLink to="/" class="font-semibold">
          <h1 class="text-xl font-bold text-black dark:text-white difference">BRX</h1>
        </NuxtLink>
      </div>

      <!-- Mobile Controls (only show on mobile when slide controls are present) -->
      <div v-if="showSlideControls" class="md:hidden flex items-center space-x-2">
        <!-- Previous Button -->
        <Button
          variant="ghost"
          size="sm"
          @click="slidesStore.previousSlide()"
          :disabled="!slidesStore.canGoPrevious"
          class="rounded-full p-1"
        >
          <ChevronLeft class="w-4 h-4" />
        </Button>
        
        <!-- Mobile slide counter -->
        <div class="bg-card/80 backdrop-blur-sm border rounded-lg px-2 py-1 text-xs font-medium">
          {{ slidesStore.currentSlideIndex + 1 }}/{{ slidesStore.totalSlides }}
        </div>
        
        <!-- Next Button -->
        <Button
          variant="ghost"
          size="sm"
          @click="slidesStore.nextSlide()"
          :disabled="!slidesStore.canGoNext"
          class="rounded-full p-1"
        >
          <ChevronRight class="w-4 h-4" />
        </Button>
        
        <!-- Mobile menu toggle -->
        <Button
          variant="ghost"
          size="sm"
          @click="toggleMobileMenu"
          @touchstart.prevent="toggleMobileMenu"
          class="rounded-full p-2"
          data-mobile-menu-toggle
        >
          <Menu v-if="!mobileMenuOpen" class="w-5 h-5" />
          <X v-else class="w-5 h-5" />
        </Button>
      </div>

      <!-- Desktop Slide Navigation Controls (only show on pitch deck page) -->
      <div v-if="showSlideControls" class="hidden md:flex items-center space-x-4">
        <!-- Presentation Clock -->
        <PresentationClock />

        <!-- Previous Button -->
        <Button
          variant="ghost"
          size="sm"
          @click="slidesStore.previousSlide()"
          :disabled="!slidesStore.canGoPrevious"
          class="rounded-full"
        >
          <ChevronLeft class="w-4 h-4" />
        </Button>

        <!-- Slide Indicators -->
        <div class="flex space-x-2">
          <button
            v-for="(slide, index) in slidesStore.slides"
            :key="slide.id"
            @click="slidesStore.goToSlide(index)"
            class="w-2 h-2 rounded-full transition-all duration-200"
            :class="[
              index === slidesStore.currentSlideIndex 
                ? 'bg-primary w-6' 
                : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
            ]"
            :title="slide.name"
          />
        </div>

        <!-- Next Button -->
        <Button
          variant="ghost"
          size="sm"
          @click="slidesStore.nextSlide()"
          :disabled="!slidesStore.canGoNext"
          class="rounded-full"
        >
          <ChevronRight class="w-4 h-4" />
        </Button>

        <!-- Slide Counter -->
        <div class="bg-card/80 backdrop-blur-sm border rounded-lg px-3 py-1 text-sm font-medium">
          {{ slidesStore.currentSlideIndex + 1 }} / {{ slidesStore.totalSlides }}
        </div>

        <!-- Edit Mode Toggle -->
        <Button
          variant="outline"
          size="sm"
          @click="slidesStore.toggleEditMode()"
          :class="[
            slidesStore.isEditMode 
              ? 'bg-primary text-primary-foreground' 
              : ''
          ]"
        >
          <Edit class="w-4 h-4 mr-2" />
          {{ slidesStore.isEditMode ? 'Exit Edit' : 'Edit' }}
        </Button>

        <!-- Fullscreen Toggle -->
        <Button
          variant="outline"
          size="sm"
          @click="toggleFullscreen"
          :title="isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'"
        >
          <Maximize2 v-if="!isFullscreen" class="w-4 h-4" />
          <Minimize2 v-else class="w-4 h-4" />
        </Button>

        <!-- Background Toggle -->
        <Button
          variant="outline"
          size="sm"
          @click="slidesStore.toggleBackground()"
          :class="[
            slidesStore.showBackground 
              ? 'bg-primary text-primary-foreground' 
              : ''
          ]"
          title="Toggle Background"
        >
          <Image class="w-4 h-4 mr-2" />
          {{ slidesStore.showBackground ? 'Hide BG' : 'Show BG' }}
        </Button>

        <!-- Before/After Overlay Toggle -->
        <Button
          variant="outline"
          size="sm"
          @click="slidesStore.toggleBeforeAfterOverlay()"
          :class="[
            slidesStore.showBeforeAfterOverlay 
              ? 'bg-primary text-primary-foreground' 
              : ''
          ]"
          title="Toggle Before/After Comparison"
        >
          <GitCompare class="w-4 h-4 mr-2" />
          {{ slidesStore.showBeforeAfterOverlay ? 'Hide Compare' : 'Compare' }}
        </Button>

        <!-- App Settings Button -->
        <Button
          variant="outline"
          size="sm"
          @click="openAppSettingsModal"
          title="App Settings"
        >
          <Settings class="w-4 h-4" />
        </Button>

        <!-- Hide Navbar Button -->
        <Button
          variant="outline"
          size="sm"
          @click="hideNavbar"
          title="Hide Navbar"
        >
          <ChevronUp class="w-4 h-4" />
        </Button>
      </div>

      <!-- Desktop Right side controls -->
      <div class="hidden md:flex items-center space-x-4">
        <!-- Manager Link (only show on pitch deck page) -->
        <Button 
          v-if="showSlideControls"
          variant="outline" 
          @click="$router.push('/ai')"
          title="AI Data Overview"
        >
          <Code2 class="w-4 h-4 mr-2" />
          Manager
        </Button>

        <Button
          v-if="!showSlideControls"
          variant="outline"
          size="sm"
          @click="openAppSettingsModal"
          title="App Settings"
        >
          <Settings class="w-4 h-4" />
        </Button>

        <!-- User Profile & Actions (from Footer) -->
        <div class="relative">
          <Button
            variant="ghost"
            size="sm"
            @click="toggleUserMenu"
            class="rounded-full p-2"
          >
            <div class="w-8 h-8 rounded-full flex items-center justify-center overflow-hidden">
              <img
                v-if="user && user.photoURL"
                :src="user.photoURL"
                alt="User profile"
                class="w-full h-full object-cover"
              />
              <UserCircle v-else class="w-6 h-6 text-muted-foreground" />
            </div>
          </Button>
          
          <!-- User Menu Dropdown -->
          <transition name="fade">
            <Card v-if="userMenuExpanded" class="absolute top-12 right-0 w-64 z-50" ref="userMenuRef">
              <CardHeader>
                <CardTitle class="text-lg flex items-center">
                  <div class="w-8 h-8 rounded-full flex items-center justify-center mr-2 overflow-hidden">
                    <img
                      v-if="user && user.photoURL"
                      :src="user.photoURL"
                      alt="User profile"
                      class="w-full h-full object-cover"
                    />
                    <UserCircle v-else class="w-6 h-6 text-muted-foreground" />
                  </div>
                  Quick Actions
                  <ModeToggle class="pl-2"/>
                </CardTitle>
                <CardDescription v-if="user">
                  {{ user.displayName || user.email }}
                </CardDescription>
                <CardDescription v-else>
                  Not logged in
                </CardDescription>
              </CardHeader>
              <CardContent class="space-y-2">
                <Button @click="goToBrxAI" class="w-full" variant="outline">
                  <ExternalLink class="mr-2 h-4 w-4" />
                  Go to BRX.AI
                </Button>
                <Button v-if="user" @click="logout" class="w-full" variant="destructive">
                  <LogOut class="mr-2 h-4 w-4" />
                  Logout
                </Button>
                <Button v-else @click="login" class="w-full" variant="default">
                  <LogIn class="mr-2 h-4 w-4" />
                  Login
                </Button>
                <p v-if="error" class="text-destructive text-sm">{{ error.message }}</p>
              </CardContent>
            </Card>
          </transition>
        </div>

        <!-- App Settings (only show when not on pitch deck page) -->

      </div>

      <!-- Mobile User Menu (only show when not on slide controls page) -->
      <div v-if="!showSlideControls" class="md:hidden">
        <Button
          variant="ghost"
          size="sm"
          @click="toggleUserMenu"
          class="rounded-full p-2"
        >
          <div class="w-8 h-8 rounded-full flex items-center justify-center overflow-hidden">
            <img
              v-if="user && user.photoURL"
              :src="user.photoURL"
              alt="User profile"
              class="w-full h-full object-cover"
            />
            <UserCircle v-else class="w-6 h-6 text-muted-foreground" />
          </div>
        </Button>
      </div>
    </div>

    <!-- Mobile Menu Dropdown -->
    <transition name="slide-down">
      <div v-if="mobileMenuOpen && showSlideControls" class="md:hidden bg-background/95 backdrop-blur-sm border-t mobile-menu-container fixed top-[60px] left-0 w-full z-[60] shadow-lg">
        <div class="px-4 py-3 space-y-3" @click.stop>
          <!-- Slide Navigation -->
          <div class="flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              @click="slidesStore.previousSlide()"
              :disabled="!slidesStore.canGoPrevious"
              class="rounded-full"
            >
              <ChevronLeft class="w-4 h-4" />
              Previous
            </Button>
            
            <Button
              variant="ghost"
              size="sm"
              @click="slidesStore.nextSlide()"
              :disabled="!slidesStore.canGoNext"
              class="rounded-full"
            >
              Next
              <ChevronRight class="w-4 h-4" />
            </Button>
          </div>

          <!-- Slide Indicators -->
          <div class="flex justify-center space-x-2 py-2">
            <button
              v-for="(slide, index) in slidesStore.slides"
              :key="slide.id"
              @click="slidesStore.goToSlide(index); mobileMenuOpen = false"
              class="w-2 h-2 rounded-full transition-all duration-200"
              :class="[
                index === slidesStore.currentSlideIndex 
                  ? 'bg-primary w-6' 
                  : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
              ]"
              :title="slide.name"
            />
          </div>

          <!-- Action Buttons -->
          <div class="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              size="sm"
              @click="slidesStore.toggleEditMode(); mobileMenuOpen = false"
              :class="[
                slidesStore.isEditMode 
                  ? 'bg-primary text-primary-foreground' 
                  : ''
              ]"
            >
              <Edit class="w-4 h-4 mr-2" />
              {{ slidesStore.isEditMode ? 'Exit Edit' : 'Edit' }}
            </Button>

            <Button
              variant="outline"
              size="sm"
              @click="toggleFullscreen; mobileMenuOpen = false"
              :title="isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'"
            >
              <Maximize2 v-if="!isFullscreen" class="w-4 h-4 mr-2" />
              <Minimize2 v-else class="w-4 h-4 mr-2" />
              {{ isFullscreen ? 'Exit' : 'Full' }}
            </Button>

            <Button
              variant="outline"
              size="sm"
              @click="slidesStore.toggleBackground(); mobileMenuOpen = false"
              :class="[
                slidesStore.showBackground 
                  ? 'bg-primary text-primary-foreground' 
                  : ''
              ]"
            >
              <Image class="w-4 h-4 mr-2" />
              {{ slidesStore.showBackground ? 'Hide BG' : 'Show BG' }}
            </Button>

            <Button
              variant="outline"
              size="sm"
              @click="slidesStore.toggleBeforeAfterOverlay(); mobileMenuOpen = false"
              :class="[
                slidesStore.showBeforeAfterOverlay 
                  ? 'bg-primary text-primary-foreground' 
                  : ''
              ]"
            >
              <GitCompare class="w-4 h-4 mr-2" />
              {{ slidesStore.showBeforeAfterOverlay ? 'Hide Compare' : 'Compare' }}
            </Button>

            <Button
              variant="outline"
              size="sm"
              @click="openAppSettingsModal; mobileMenuOpen = false"
            >
              <Settings class="w-4 h-4 mr-2" />
              Settings
            </Button>
          </div>

          <!-- Additional Actions -->
          <div class="space-y-2">
            <Button 
              variant="outline" 
              @click="$router.push('/ai'); mobileMenuOpen = false"
              class="w-full"
            >
              <Code2 class="w-4 h-4 mr-2" />
              Manager
            </Button>

            <Button
              variant="outline"
              size="sm"
              @click="hideNavbar"
              class="w-full"
            >
              <ChevronUp class="w-4 h-4 mr-2" />
              Hide Navbar
            </Button>
          </div>

          <!-- User Actions -->
          <div class="border-t pt-3 space-y-2">
            <div class="flex items-center space-x-2 mb-2">
              <div class="w-8 h-8 rounded-full flex items-center justify-center overflow-hidden">
                <img
                  v-if="user && user.photoURL"
                  :src="user.photoURL"
                  alt="User profile"
                  class="w-full h-full object-cover"
                />
                <UserCircle v-else class="w-6 h-6 text-muted-foreground" />
              </div>
              <div class="flex-1">
                <p class="text-sm font-medium">{{ user?.displayName || user?.email || 'Not logged in' }}</p>
              </div>
              <ModeToggle />
            </div>
            
            <Button @click="goToBrxAI; mobileMenuOpen = false" class="w-full" variant="outline">
              <ExternalLink class="mr-2 h-4 w-4" />
              Go to BRX.AI
            </Button>
            
            <Button v-if="user" @click="logout; mobileMenuOpen = false" class="w-full" variant="destructive">
              <LogOut class="mr-2 h-4 w-4" />
              Logout
            </Button>
            <Button v-else @click="login; mobileMenuOpen = false" class="w-full" variant="default">
              <LogIn class="mr-2 h-4 w-4" />
              Login
            </Button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Show/Hide Toggle Button -->
    <div 
      v-if="isHidden"
      class="fixed top-0 left-1/2 transform -translate-x-1/2 z-50 bg-card/80 backdrop-blur-sm border border-t-0 rounded-b-lg px-3 py-1 cursor-pointer hover:bg-card transition-colors"
      @click="showNavbar"
    >
      <ChevronDown class="w-4 h-4 text-muted-foreground" />
    </div>

    <!-- Mobile Minimal Controls (when navbar is hidden) -->
    <div 
      v-if="isHidden && showSlideControls" 
      class="md:hidden fixed top-16 right-4 z-50 flex items-center space-x-2"
    >
      <!-- Previous Button -->
      <Button
        variant="ghost"
        size="sm"
        @click="slidesStore.previousSlide()"
        :disabled="!slidesStore.canGoPrevious"
        class="rounded-full p-2 bg-card/90 backdrop-blur-sm border shadow-lg hover:bg-card"
      >
        <ChevronLeft class="w-4 h-4" />
      </Button>

      <!-- Slide Counter -->
      <div class="bg-card/90 backdrop-blur-sm border rounded-full px-3 py-2 text-xs font-medium shadow-lg">
        {{ slidesStore.currentSlideIndex + 1 }}/{{ slidesStore.totalSlides }}
      </div>

      <!-- Next Button -->
      <Button
        variant="ghost"
        size="sm"
        @click="slidesStore.nextSlide()"
        :disabled="!slidesStore.canGoNext"
        class="rounded-full p-2 bg-card/90 backdrop-blur-sm border shadow-lg hover:bg-card"
      >
        <ChevronRight class="w-4 h-4" />
      </Button>

      <!-- Show Navbar/Menu Button -->
      <Button
        variant="ghost"
        size="sm"
        @click="showNavbar"
        class="rounded-full p-2 bg-card/90 backdrop-blur-sm border shadow-lg hover:bg-card"
      >
        <Menu class="w-4 h-4" />
      </Button>
    </div>
  </header>
</template>

<script setup lang="ts">
import { inject, computed, ref, onMounted, onUnmounted, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import ModeToggle from '~/components/ModeToggle.vue'
import PresentationClock from '~/components/PresentationClock.vue'
import { useSlidesStore } from '~/stores/slides'
import { useAuth } from '@/composables/useAuth'
import { ArrowLeft, Code2, Edit, X, RefreshCw, CirclePlus, Settings, ChevronLeft, ChevronRight, Maximize2, Minimize2, ChevronDown, ChevronUp, UserCircle, ExternalLink, LogOut, LogIn, Image, Menu, GitCompare } from 'lucide-vue-next'

const openAppSettingsModal = inject('openCreateProjectPopup')
const route = useRoute()
const slidesStore = useSlidesStore()
const { user, error, logout, login } = useAuth()

// Show slide controls only on the main pitch deck page
const showSlideControls = computed(() => {
  return route.path === '/'
})

// Navbar hide/show functionality
const isHidden = ref(false)
const lastScrollY = ref(0)
const hideTimeout = ref<NodeJS.Timeout>()

const handleScroll = () => {
  const currentScrollY = window.scrollY
  
  if (currentScrollY > lastScrollY.value && currentScrollY > 100) {
    // Scrolling down and past threshold - hide navbar
    if (hideTimeout.value) clearTimeout(hideTimeout.value)
    hideTimeout.value = setTimeout(() => {
      isHidden.value = true
    }, 150)
  } else if (currentScrollY < lastScrollY.value) {
    // Scrolling up - show navbar
    if (hideTimeout.value) clearTimeout(hideTimeout.value)
    isHidden.value = false
  }
  
  lastScrollY.value = currentScrollY
}

const showNavbar = () => {
  isHidden.value = false
  // Dispatch event to sync with SlideContainer
  window.dispatchEvent(new CustomEvent('navbar-toggle', { 
    detail: { isHidden: false } 
  }))
}

const hideNavbar = () => {
  isHidden.value = true
  mobileMenuOpen.value = false // Close mobile menu when hiding navbar
  // Dispatch event to sync with SlideContainer
  window.dispatchEvent(new CustomEvent('navbar-toggle', { 
    detail: { isHidden: true } 
  }))
}

// Mobile menu functionality
const mobileMenuOpen = ref(false)

const toggleMobileMenu = (event?: Event) => {
  event?.stopPropagation()
  mobileMenuOpen.value = !mobileMenuOpen.value
  
  // Close mobile menu when clicking outside
  if (mobileMenuOpen.value) {
    setTimeout(() => {
      document.addEventListener('click', handleMobileMenuClickOutside, { once: true })
    }, 100)
  }
}

const handleMobileMenuClickOutside = (event: MouseEvent) => {
  // Close mobile menu when clicking outside
  const target = event.target as HTMLElement
  if (!target.closest('.mobile-menu-container') && !target.closest('[data-mobile-menu-toggle]')) {
    mobileMenuOpen.value = false
    document.removeEventListener('click', handleMobileMenuClickOutside)
  }
}

// User menu functionality (from Footer)
const userMenuExpanded = ref(false)
const userMenuRef = ref<HTMLElement | null>(null)

const toggleUserMenu = () => {
  userMenuExpanded.value = !userMenuExpanded.value
  if (userMenuExpanded.value) {
    nextTick(() => {
      document.addEventListener('click', handleUserMenuClickOutside)
    })
  } else {
    document.removeEventListener('click', handleUserMenuClickOutside)
  }
}

const goToBrxAI = () => {
  window.open('https://brx.ai', '_blank')
  userMenuExpanded.value = false
}

const handleUserMenuClickOutside = (event: MouseEvent) => {
  if (userMenuRef.value instanceof HTMLElement && !userMenuRef.value.contains(event.target as Node) && !event.defaultPrevented) {
    userMenuExpanded.value = false
    document.removeEventListener('click', handleUserMenuClickOutside)
  }
}

// Fullscreen functionality
const isFullscreen = ref(false)

const toggleFullscreen = async () => {
  try {
    if (!document.fullscreenElement) {
      await document.documentElement.requestFullscreen()
      isFullscreen.value = true
    } else {
      await document.exitFullscreen()
      isFullscreen.value = false
    }
  } catch (error) {
    console.error('Error toggling fullscreen:', error)
  }
}

const handleFullscreenChange = () => {
  isFullscreen.value = !!document.fullscreenElement
}

// Keyboard shortcuts for navbar control
const handleKeydown = (event: KeyboardEvent) => {
  // Press 'h' or 'H' to toggle navbar visibility
  if (event.key === 'h' || event.key === 'H') {
    if (!event.ctrlKey && !event.metaKey && !event.altKey) {
      // Only if no modifier keys are pressed
      const activeElement = document.activeElement
      // Don't trigger if user is typing in an input or contenteditable element
      if (activeElement?.tagName !== 'INPUT' && 
          activeElement?.tagName !== 'TEXTAREA' && 
          !activeElement?.hasAttribute('contenteditable')) {
        event.preventDefault()
        if (isHidden.value) {
          showNavbar()
        } else {
          hideNavbar()
        }
      }
    }
  }
  
  // Close mobile menu on escape
  if (event.key === 'Escape') {
    mobileMenuOpen.value = false
  }
}

onMounted(() => {
  document.addEventListener('fullscreenchange', handleFullscreenChange)
  document.addEventListener('keydown', handleKeydown)
  window.addEventListener('scroll', handleScroll, { passive: true })
})

onUnmounted(() => {
  document.removeEventListener('fullscreenchange', handleFullscreenChange)
  document.removeEventListener('keydown', handleKeydown)
  window.removeEventListener('scroll', handleScroll)
  document.removeEventListener('click', handleUserMenuClickOutside)
  document.removeEventListener('click', handleMobileMenuClickOutside)
  if (hideTimeout.value) clearTimeout(hideTimeout.value)
})
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
}
.slide-down-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.mobile-menu-container {
  /* This class is used for click outside detection */
}
</style>
